// Mesón · fase 2a (docs/frontend/19): TURNOS Y CIERRES para el supervisor / tesorería. Lista los
// turnos (por revisar primero), con la diferencia neta del arqueo; el detalle muestra lo esperado en
// vivo de un turno abierto, o el arqueo de uno cerrado, sus cobros y retiros, y la REVISIÓN:
// conforme, o con reparo (efectivo recontado + motivo). Nadie revisa su propio turno (lo exige la API).
//
// 0130 · una caja con POLÍTICA de cierre no pasa por esta revisión: su cierre es NORMAL (no queda
// pendiente) o una EXCEPCIÓN que se resuelve en «Excepciones de cierre». Las demás cajas siguen igual.
// El detalle muestra quién cobró cada recibo, los operadores del turno y su auditoría.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { claves, useTurno, useTurnos } from '../api/consultas';
import type { ClaseCierre, ResumenExcepcion, TurnoCaja } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { AuditoriaTurno } from './AuditoriaTurno';
import { NOMBRE_MEDIO_CAJA, TablaArqueo, enteroCaja } from './MiTurno';

const FILTROS = [
  { valor: 'CERRADO', nombre: 'Por revisar' },
  { valor: 'ABIERTO', nombre: 'Abiertos' },
  { valor: 'REVISADO', nombre: 'Revisados' },
  { valor: '', nombre: 'Todos' },
] as const;

export function textoEstadoTurno(
  estado: string,
  revision: string | null,
  cierreClase: ClaseCierre | null = null,
  excepcion: ResumenExcepcion | null = null,
) {
  if (estado === 'ABIERTO') return 'abierto';
  // 0130: con política de cierre, el control es por excepciones.
  if (cierreClase === 'NORMAL') return 'cierre normal';
  if (cierreClase === 'EXCEPCION')
    return excepcion?.estado === 'RESUELTA'
      ? 'excepción resuelta'
      : `excepción · nivel ${excepcion?.nivel ?? '—'}`;
  if (estado === 'CERRADO') return 'por revisar';
  return revision === 'CON_REPARO' ? 'revisado · con reparo' : 'revisado · conforme';
}

export function TurnosCaja({ activa }: { activa: boolean }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState<string>('CERRADO');
  const [elegido, setElegido] = useState<string | null>(null);
  const lista = useTurnos(empresaId, estado, activa);
  return (
    <div className="pantalla consulta" data-pantalla="turnos-caja">
      <h2>Turnos de caja y cierres</h2>
      <div className="acciones" role="group" aria-label="Filtro de turnos">
        {FILTROS.map((f) => (
          <button
            key={f.valor}
            type="button"
            aria-pressed={estado === f.valor}
            className={estado === f.valor ? 'primario' : undefined}
            onClick={() => {
              setEstado(f.valor);
              setElegido(null);
            }}
          >
            {f.nombre}
          </button>
        ))}
      </div>
      {lista.isError && <PanelError error={lista.error} />}
      <table className="grilla compacta" aria-label="Turnos de caja">
        <thead>
          <tr>
            <th>Turno</th>
            <th>Punto</th>
            <th>Cajero</th>
            <th>Abierto</th>
            <th>Cerrado</th>
            <th>Estado</th>
            <th className="al-der">Diferencia</th>
          </tr>
        </thead>
        <tbody>
          {(lista.data ?? [])
            // «Por revisar» es la revisión de siempre: los cierres con política no esperan a nadie.
            .filter((t) => estado !== 'CERRADO' || !t.cierreClase)
            .map((t) => {
              const d = t.diferencia === null ? null : Number(t.diferencia);
              return (
                <tr
                  key={t.id}
                  data-turno={t.numeroVisible}
                  aria-selected={elegido === t.id}
                  className={elegido === t.id ? 'seleccionada' : undefined}
                >
                  <td>
                    <button type="button" className="enlace mono" onClick={() => setElegido(t.id)}>
                      {t.numeroVisible}
                    </button>
                  </td>
                  <td>{t.punto}</td>
                  <td>{t.cajero}</td>
                  <td>{fechaHora(t.abiertoEn)}</td>
                  <td>{t.cerradoEn ? fechaHora(t.cerradoEn) : '—'}</td>
                  <td>
                    <span className="insignia" data-estado-turno={t.estado}>
                      {textoEstadoTurno(t.estado, t.revision, t.cierreClase, t.excepcion)}
                    </span>
                  </td>
                  <td
                    className="al-der"
                    data-diferencia={
                      d === null ? 'no-aplica' : d === 0 ? 'cuadra' : d < 0 ? 'falta' : 'sobra'
                    }
                  >
                    {d === null ? '—' : d === 0 ? 'cuadra' : pesos(t.diferencia)}
                  </td>
                </tr>
              );
            })}
          {lista.isSuccess && !lista.data.length && (
            <tr>
              <td colSpan={7} className="grilla-vacia">
                No hay turnos {FILTROS.find((f) => f.valor === estado)?.nombre.toLowerCase()}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {elegido && (
        <DetalleTurno
          empresaId={empresaId}
          turnoId={elegido}
          puedeRevisar={puede('caja.turno.revisar')}
        />
      )}
    </div>
  );
}

function DetalleTurno({
  empresaId,
  turnoId,
  puedeRevisar,
}: {
  empresaId: string;
  turnoId: string;
  puedeRevisar: boolean;
}) {
  const q = useTurno(empresaId, turnoId);
  const [revisando, setRevisando] = useState(false);
  const [auditando, setAuditando] = useState(false);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return null;
  const t = q.data;
  return (
    <section className="caja-detalle" aria-label={`Detalle del turno ${t.numeroVisible}`}>
      <h3>
        {t.numeroVisible} · {t.punto.codigo} · {t.cajero}
      </h3>
      <p>
        Abierto {fechaHora(t.abiertoEn)}
        {t.cerradoEn ? ` · cerrado ${fechaHora(t.cerradoEn)}` : ''} · fondo inicial{' '}
        <b>{pesos(t.fondoInicial)}</b>
        {t.observacionCierre ? ` · «${t.observacionCierre}»` : ''}
      </p>
      {t.cierreClase && (
        <p data-clase-cierre={t.cierreClase}>
          {textoEstadoTurno(t.estado, null, t.cierreClase, t.excepcion ?? null)}
          {t.cierreClase === 'NORMAL' ? ': cuadró sin incidentes, no queda pendiente.' : ''}
        </p>
      )}
      {!!t.operadores?.length && (
        <table className="grilla compacta" aria-label="Operadores del turno">
          <caption>Turno compartido: operadores habilitados por la responsable</caption>
          <tbody>
            {t.operadores.map((o) => (
              <tr key={`${o.usuarioId}-${o.autorizadoEn}`}>
                <td>{o.nombre}</td>
                <td>
                  habilitado {fechaHora(o.autorizadoEn)} por {o.autorizadoPorNombre}
                </td>
                <td>{o.retiradoEn ? `retirado ${fechaHora(o.retiradoEn)}` : 'operando'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {t.esperadoEnVivo && (
        <table className="grilla compacta" aria-label="Esperado en vivo">
          <caption>Esperado en vivo (el cajero no lo ve: cierra a ciegas)</caption>
          <tbody>
            {t.esperadoEnVivo.map((e) => (
              <tr key={e.medio}>
                <td>{NOMBRE_MEDIO_CAJA[e.medio]}</td>
                <td className="al-der">{pesos(e.esperado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {t.arqueo && <TablaArqueo arqueo={t.arqueo} />}
      {t.aRendirEfectivo !== null && (
        <p>
          Efectivo a rendir al cierre:{' '}
          <b>{pesos(String(Math.max(0, Number(t.aRendirEfectivo))))}</b> · retirado durante el
          turno:{' '}
          <b>
            {pesos(
              String(
                t.movimientos
                  .filter((m) => m.tipo === 'RETIRO')
                  .reduce((s, m) => s + Number(m.monto), 0),
              ),
            )}
          </b>
        </p>
      )}
      {t.revision && (
        <p data-revision={t.revision.resultado}>
          Revisado por {t.revision.por} ({fechaHora(t.revision.en)}):{' '}
          <b>{t.revision.resultado === 'CONFORME' ? 'conforme' : 'con reparo'}</b>
          {t.revision.recontadoEfectivo !== null &&
            ` · efectivo recontado ${pesos(t.revision.recontadoEfectivo)}`}
          {t.revision.nota ? ` · «${t.revision.nota}»` : ''}
        </p>
      )}
      <details>
        <summary>
          {t.recibos.length} cobro{t.recibos.length === 1 ? '' : 's'} · {t.movimientos.length}{' '}
          retiro{t.movimientos.length === 1 ? '' : 's'}/ingreso
          {t.movimientos.length === 1 ? '' : 's'}
        </summary>
        <table className="grilla compacta">
          <tbody>
            {t.recibos.map((r) => (
              <tr key={r.id} className={r.estado === 'ANULADO' ? 'anulado' : undefined}>
                <td>{fechaHora(r.creadoEn)}</td>
                <td className="mono">{r.numeroVisible}</td>
                <td className="mono">{r.nota ?? '—'}</td>
                <td>{r.cobradoPor?.nombre ?? ''}</td>
                <td>
                  {r.medios
                    .map((m) => `${NOMBRE_MEDIO_CAJA[m.medio]} ${pesos(m.monto)}`)
                    .join(' + ')}
                </td>
                <td className="al-der">{pesos(r.total)}</td>
              </tr>
            ))}
            {t.movimientos.map((m) => (
              <tr key={m.id}>
                <td>{fechaHora(m.creadoEn)}</td>
                <td>{m.tipo === 'RETIRO' ? 'Retiro' : 'Ingreso'}</td>
                <td colSpan={3}>{m.motivo}</td>
                <td className="al-der">
                  {m.tipo === 'RETIRO' ? '−' : '+'}
                  {pesos(m.monto)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <footer className="acciones">
        <button type="button" onClick={() => setAuditando(true)}>
          Auditoría del turno
        </button>
        {t.estado === 'CERRADO' && puedeRevisar && !t.cierreClase && (
          <button type="button" className="primario" onClick={() => setRevisando(true)}>
            Revisar cierre…
          </button>
        )}
      </footer>
      {revisando && <RevisarTurno turno={t} alCerrar={() => setRevisando(false)} />}
      {auditando && (
        <AuditoriaTurno empresaId={empresaId} turnoId={t.id} alCerrar={() => setAuditando(false)} />
      )}
    </section>
  );
}

function RevisarTurno({ turno, alCerrar }: { turno: TurnoCaja; alCerrar: () => void }) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [resultado, setResultado] = useState<'CONFORME' | 'CON_REPARO'>('CONFORME');
  const [recontado, setRecontado] = useState(
    String(Math.max(0, Number(turno.aRendirEfectivo ?? '0'))),
  );
  const [nota, setNota] = useState('');
  const monto = enteroCaja(recontado);
  const valido = resultado === 'CONFORME' || (monto !== null && nota.trim().length >= 3);
  const revisar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        version: turno.version,
        resultado,
        ...(resultado === 'CON_REPARO' ? { recontadoEfectivo: monto! } : {}),
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      };
      return api.post(`/caja/turnos/${turno.id}/revisar`, cuerpo, {
        idempotencia: intentos.clave('revisar-turno', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(turno.empresaId) });
      alCerrar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  return (
    <Modal
      titulo={`Revisar cierre · ${turno.numeroVisible}`}
      ancho="angosto"
      alCerrar={() => !revisar.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={revisar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido || revisar.isPending}
            onClick={() => revisar.mutate()}
          >
            {revisar.isPending
              ? 'Guardando…'
              : resultado === 'CONFORME'
                ? 'Marcar conforme'
                : 'Guardar con reparo'}
          </button>
        </>
      }
    >
      <p className="apagado">
        Recuenta el efectivo que te rindió el cajero. Si calza, conforme; si no, anota lo recontado
        y por qué.
      </p>
      <fieldset className="campo caja-revision">
        <legend className="etiqueta">Resultado</legend>
        <label>
          <input
            type="radio"
            name="resultado"
            data-autofoco
            checked={resultado === 'CONFORME'}
            onChange={() => setResultado('CONFORME')}
          />{' '}
          Conforme
        </label>{' '}
        <label>
          <input
            type="radio"
            name="resultado"
            checked={resultado === 'CON_REPARO'}
            onChange={() => setResultado('CON_REPARO')}
          />{' '}
          Con reparo
        </label>
      </fieldset>
      {resultado === 'CON_REPARO' && (
        <label className="campo">
          <span className="etiqueta">Efectivo recontado</span>
          <input
            aria-label="Efectivo recontado"
            inputMode="numeric"
            className="cobro-monto"
            value={recontado}
            onChange={(e) => setRecontado(e.target.value)}
          />
        </label>
      )}
      <label className="campo">
        <span className="etiqueta">
          Nota{resultado === 'CON_REPARO' ? ' (obligatoria)' : ' (opcional)'}
        </span>
        <input
          aria-label="Nota de la revisión"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
      </label>
      {revisar.isError && <PanelError error={revisar.error} />}
    </Modal>
  );
}
